import React, { memo, useCallback, useContext, useEffect, useState } from "react";
import { HEADER_LOTTIE_ASSETS, LottieAnimation } from "@swiggy-private/core-ui";
import { CART_HEADER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartHeaderContext, INITIAL_CART_HEADER_CONTEXT_VALUE } from "@cart-client/contexts/CartHeaderContext";

// constants
import { BoltLottieFrames, BoltLottieTransitionFrame, lottieProps } from "./constants";

// styles
import styles from "../Header.scss";

// styled
import { BoltContainer } from "./styled";
import { BoltLottieJson, BoltLottieProps } from "@cart-client/interfaces/BoltLottie";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

export const BoltLottie = ({
    showBoltLottie,
    sla,
    isRangeSla,
    setBoltLottieShown,
}: BoltLottieProps): JSX.Element | null => {
    const [hideLottie, setHideLottie] = useState(false);
    const { setCartHeaderState } = useContext(CartHeaderContext);
    const [updatedJson, setUpdatedJson] = useState<unknown>();

    const lottieAsset = isRangeSla
        ? HEADER_LOTTIE_ASSETS.BOLT_RANGE_SLA_TRANSITION
        : HEADER_LOTTIE_ASSETS.BOLT_TRANSITION;

    const onLottieCompletion = useCallback(() => {
        setHideLottie(true);
        setBoltLottieShown(true);
    }, [setBoltLottieShown]);

    const handleInitiateTheme = useCallback(() => {
        setCartHeaderState(prev => {
            if (!prev.hideTheme) {
                return { ...prev, themeChange: true };
            }
            return prev;
        });
    }, [setCartHeaderState]);

    const handleError = useCallback(() => {
        setCartHeaderState({
            ...INITIAL_CART_HEADER_CONTEXT_VALUE.cartHeaderState,
            hideTheme: true,
        });
        setHideLottie(true);
    }, [setCartHeaderState]);

    const fetchAndUpdateSla = useCallback(
        async (sla: string) => {
            const lottieUrl = getCloudinaryUrl(lottieAsset) || "";

            try {
                const response = await fetch(lottieUrl);
                if (response.ok) {
                    const parsedLottieJson = (await response.json()) as BoltLottieJson;

                    if (isRangeSla) {
                        setUpdatedJson(parsedLottieJson);
                        return;
                    }

                    const haveSlaKeys =
                        !isEmpty(parsedLottieJson) &&
                        !isEmpty(parsedLottieJson.layers) &&
                        !isEmpty(parsedLottieJson.layers?.[2]) &&
                        !isEmpty(parsedLottieJson.layers?.[2].t?.d?.k?.[0]?.s);

                    if (haveSlaKeys && parsedLottieJson.layers?.[2]) {
                        const layer = parsedLottieJson.layers[2];
                        if (layer.t?.d?.k?.[0]?.s) {
                            layer.nm = sla;
                            layer.t.d.k[0].s.t = sla;
                            setUpdatedJson(parsedLottieJson);
                        }
                    }
                }
            } catch (error) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.CART_LOTTIE_ERROR,
                    url: lottieAsset,
                    error: error instanceof Error ? error.name : JSON.stringify(error),
                    errorMessage: error instanceof Error ? error.message : JSON.stringify(error),
                    sla: sla.toString(),
                    componentName: "BoltLottie",
                });
                return;
            }
        },
        [isRangeSla, lottieAsset],
    );

    useEffect(() => {
        if (sla <= 10) {
            fetchAndUpdateSla("10");
        } else {
            fetchAndUpdateSla("15");
        }
    }, [fetchAndUpdateSla, sla]);

    if (hideLottie || !updatedJson) {
        return null;
    }

    return (
        <BoltContainer data-testid={CART_HEADER_DATA_TEST_IDS.BOLT_LOTTIE}>
            <LottieAnimation
                {...lottieProps}
                visible={showBoltLottie}
                playLottie={showBoltLottie}
                autoplay={showBoltLottie}
                className={styles.lottie}
                onPlayComplete={onLottieCompletion}
                onFrameEventCompletion={handleInitiateTheme}
                onError={handleError}
                animationData={updatedJson}
                frameEventBreak={BoltLottieTransitionFrame}
                enableFrameEvent={true}
                preserveAspect="xMinYMid slice"
                frame={BoltLottieFrames}
            />
        </BoltContainer>
    );
};

export default memo(BoltLottie);
